import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';
import { App } from '../services/app/app';
import { ErrorDialogService } from '../services/error-dialog/error-dialog';
import { ApiCircuitBreakerService } from '../services/api-circuit-breaker/api-circuit-breaker';
import { SessionReloginService } from '../services/session/session-relogin.service';

export const apiErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const appService      = inject(App);
  const errorDialog     = inject(ErrorDialogService);
  const circuitBreaker  = inject(ApiCircuitBreakerService);
  const sessionRelogin  = inject(SessionReloginService);
  const requestUrl      = req.urlWithParams || req.url;

  if (!circuitBreaker.canRequest(requestUrl)) {
    const remainingSeconds = Math.ceil(circuitBreaker.timeRemainingMs() / 1000);
    appService.setLoading(false);
    errorDialog.openCircuitBreakerOpen(remainingSeconds);

    return throwError(
      () =>
        new HttpErrorResponse({
          status: 0,
          statusText: 'Circuit Breaker Open',
          url: requestUrl,
          error: {
            circuitBreakerOpen: true,
            message: 'Requests are temporarily blocked while the service recovers.'
          }
        })
    );
  }

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        circuitBreaker.recordSuccess(requestUrl);
      }
    }),
    catchError((err: unknown) => {
      // Always stop the global loading spinner on API failure
      appService.setLoading(false);

      const httpError = err instanceof HttpErrorResponse ? err : null;

      // ── 401 Unauthorized → session expired, not a generic API error ────────
      if (httpError?.status === 401) {
        errorDialog.openSessionExpired(() => sessionRelogin.trigger());
        return throwError(() => err);
      }

      // ── All other errors go through circuit breaker as before ──────────────
      const breakerState = httpError
        ? circuitBreaker.recordFailure(httpError, requestUrl)
        : { tripped: false };

      if (breakerState.tripped) {
        const remainingSeconds = Math.ceil(circuitBreaker.timeRemainingMs() / 1000);
        errorDialog.openCircuitBreakerOpen(remainingSeconds);
        return throwError(() => err);
      }

      const message = httpError ? extractErrorMessage(httpError) : undefined;
      const supportCode = httpError ? extractSupportCode(httpError) : undefined;

      errorDialog.openApiError(message, supportCode);
      return throwError(() => err);
    })
  );
};

function extractErrorMessage(error: HttpErrorResponse): string | undefined {
  if (typeof error.error === 'string') {
    return error.error;
  }

  return error.error?.message || error.message || undefined;
}

function extractSupportCode(error: HttpErrorResponse): string | undefined {
  const body = error.error;

  if (!body || typeof body !== 'object') {
    return undefined;
  }

  return body.supportCode
    || body.support_code
    || body.requestId
    || body.request_id
    || body.correlationId
    || body.correlation_id
    || undefined;
}
